// creature-actions.ts — creature-state MUTATION handlers (template.js cleanup roadmap T3.1, 2026-06-14).
//
// Byte-faithful port of the creature-action layer carved out of template.js. Combat-relevant —
// ported verbatim; only `: any` params + `as HTMLInputElement` DOM casts + `!` non-null assertions
// were added for strict TS. Event-handler functions preserve their `event` arg exactly.
//
// Public surface (all window-attached — PHP onclick + cross-bundle onclick strings + template.js
// bootstrap callers, all runtime-invoked):
//   modifyEncounterCreature, randomizeEncounterCreatureName, addAPToCreature, addReactionToCreature,
//   killCreature, destroyCreature, resurrectCreature, setCasterSummon, ftEnsureSummonSheetLoaded,
//   addAttackToCaster, reduceAttackToCaster, addHeadAttackToCaster, reduceHeadAttackToCaster,
//   update_encounter_creature_pic_id, updatePreferredSpeedType.
// Callers: options/tools/encounter_creatures/forms/form_modify_encounter_creature_inc.php (onchange),
//   options/game/character_sheet_display/** (abilities / weapons_equipped / summons onclick),
//   options/tools/encounters/reports/encounter_creatures_report_edit_inc.php, the object-card /
//   info-panel / player-character-sheet / shared(build-carousel) render bundles (onclick strings +
//   w.ftEnsureSummonSheetLoaded), includes/functions/janus/core/ability_effect_speed_renderers.php,
//   and template.js's shown.bs.tab listener + loadPostFunctions (ftEnsureSummonSheetLoaded — OUTSIDE
//   the carved range, so it MUST stay window-attached).
//
// reduceHeadAttackToCaster was NOT in the Tier-3 plan's T3.1 list (oversight) but is the symmetric
// partner of addHeadAttackToCaster and travels with the head-attack cluster — carved here for cohesion.
//
// Loaded AFTER template.js; every dep resolves at call time (onclick / form-button / AJAX / tab show):
// BASE_URL / queryData / queryDataInputs / actionRefresh / refreshCasterSheet / loadPostFunctions /
// getSidelogContext / validateContext / forceInputChange / ftDebugLog / PREFERRED_SPEED_TO_MODE
// (template.js), getCheckboxElementValue / checkIfValidUUID / setCookie (core-helpers), Quill (vendor),
// subscribeToCaster (websocket bundle).

import { creatureNameTables, generateCreatureName } from '../janus-engine/advancement/random-creature-name';
import { characterCopyVitals, planSwarmReinforcement } from '../janus-engine/advancement/creature-tools';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { preferredSpeedSaveRefused, preferredSpeedTargets, switchPreferredSpeed } from './preferred-speed-hold';
import { ftCurrentTruthSink } from '../shared/truth-sink';
import { encounterCreatureField } from '../shared/array-types';
import { creatureDefinitionMaximum } from '../shared/spawn-browser-bridge';
import { withAsyncDeadline } from '../shared/async-deadline';
import { templateChangeVitality } from '../janus-engine/spawn/vitality';
import { refuseUnported } from '../janus-engine/unported';

type CreatureActionRecord = Record<string, unknown>;
interface CreatureActionHost {
    FateTide?: { casterCache?: Map<string, unknown>; encounterCache?: Map<string, unknown>; masterCasterId?: string };
    ftGetActiveCasterId?: () => string;
    ftSeedCasterFromEncounterCache?: (id: string) => boolean;
    ftSetActiveCaster?: (id: string) => unknown | Promise<unknown>;
    ftSetSelectedSummon?: (id: string) => void;
    ftCancelEncounterPicRebuildFallback?: (encounterId: string) => void;
    /** Lane GMS S9 (websocket/status-truth.ts): client-truth GM kill / resurrect / destroy; null = run the legacy endpoint. */
    ftCommitCreatureLife?: (input: { actorId: string; action: 'kill' | 'resurrect' | 'destroy' | 'flee'; killerId?: string; encounterId?: string }) => Promise<'handled' | 'refused' | null>;
}
function creatureActionHost(): Window & CreatureActionHost {
    return window as unknown as Window & CreatureActionHost;
}
function recordOf(value: unknown): CreatureActionRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as CreatureActionRecord : null;
}
function formControlValue(container: ParentNode, selector: string): string {
    const el = container.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(selector);
    return el?.value ?? '';
}
const modifySaveEpochs = new WeakMap<HTMLElement, object>();

async function modifyEncounterCreature(event: Event): Promise<void> {
    ftDebugLog('modifyEncounterCreature', { called: true });

    // Find the closest div containing the button that triggered this function
    const container = event.target instanceof Element ? event.target.closest<HTMLElement>('.encounter-creature-container-form') : null;

    if (!container) {
        console.error('No valid encounter creature container found.');
        return;
    }
    const saveEpoch = {};
    modifySaveEpochs.set(container, saveEpoch);

    // Manually sync Quill content
    ['encounter_creature_flavor_text', 'encounter_creature_personality', 'encounter_creature_quirks', 'encounter_creature_motivations', 'encounter_creature_secrets', 'encounter_creature_tactics', 'encounter_creature_gm_notes'].forEach(id => {
        const editor = container.querySelector(`.quill-editor[data-layer-type-name="${id}"]`);
        const textarea = container.querySelector<HTMLTextAreaElement>(`#${id}`);
        if (editor && textarea) {
            const quill = Quill.find(editor);
            if (quill) {
                textarea.value = quill.root.innerHTML;
                ftDebugLog('modifyEncounterCreature', { syncedQuill: id, valueLen: textarea.value?.length });
            } else if (editor.classList.contains('quill-initialized')) {
                // Editor claims initialized but Quill.find returned null — genuine bug.
                console.error(`No Quill instance found for ${id}`);
            }
            // else: Quill init hasn't run yet. loadPostFunctions calls initializeTomSelect before
            // initializeQuillEditors; Tom Select's init can dispatch change events on selects
            // whose legacy inline change hook can fire before the editors mount.
            // The textarea retains its server-rendered value, so the reads below still work.
        } else {
            console.error(`Editor or textarea missing for ${id}`, { editor, textarea });
        }
    });

    const encounter_creature_id = formControlValue(container, '#input_hidden_encounter_creature_id');
    const encounter_creature_name = formControlValue(container, '#encounter_creature_name');
    const encounter_creature_flavor_text = formControlValue(container, '#encounter_creature_flavor_text');
    const encounter_creature_personality = formControlValue(container, '#encounter_creature_personality');
    const encounter_creature_quirks = formControlValue(container, '#encounter_creature_quirks');
    const encounter_creature_motivations = formControlValue(container, '#encounter_creature_motivations');
    const encounter_creature_secrets = formControlValue(container, '#encounter_creature_secrets');
    const encounter_creature_tactics = formControlValue(container, '#encounter_creature_tactics');
    const encounter_creature_gm_notes = formControlValue(container, '#encounter_creature_gm_notes');
    // encounter_creature_private is NOT read or sent (fixed 2026-07-15). This form paints no
    // #encounter_creature_private control — privacy is owned by the privateButtons() toggle ->
    // update_privacy_inc.php. The old read here resolved querySelector(...) to null, and
    // getCheckboxElementValue(null) returns its default 0, so EVERY save posted private=0 and
    // republished any hidden creature. modify_encounter_creature_inc.php now sources privacy from the
    // row and ignores the payload; sending nothing keeps the wire honest about what this form edits.
    const team = formControlValue(container, '#team');
    const controller_encounter_creature_id = formControlValue(container, '#controller_encounter_creature_id');
    const summon_type = formControlValue(container, '#summon_type');
    const creature_number = formControlValue(container, '#creature_number');
    const creature_initiative = formControlValue(container, '#creature_initiative');
    const v_current = formControlValue(container, '#v_current');
    const v_max = formControlValue(container, '#v_max');
    const v_temp = formControlValue(container, '#v_temp');
    const v_max_temp = formControlValue(container, '#v_max_temp');
    const encounter_creature_dead = getCheckboxElementValue(container.querySelector('#encounter_creature_dead'));
    const spell_list_prepared = formControlValue(container, '#spell_list_prepared');
    const mana = formControlValue(container, '#mana');
    const aps = formControlValue(container, '#aps');
    const aps_total = formControlValue(container, '#aps_total');
    const x_pos = formControlValue(container, '#x_pos');
    const z_pos = formControlValue(container, '#z_pos');
    const gender = formControlValue(container, '#gender');
    // VoiceForge: encounter_creatures.voice_id. Optional — '' means "no voice", which the endpoint
    // normalizes to NULL and the cast resolver reads as silence.
    const voice_id = formControlValue(container, '#voice_id');
    const merchantFavorInput = container.querySelector<HTMLInputElement>('#merchant_favor');
    const encounterCreatureMonsterTemplateIdInput = container.querySelector<HTMLInputElement>(
        '#encounter_creature_monster_template_id',
    );

    const payload: Record<string, unknown> = {
                encounter_creature_id,
                encounter_creature_name,
                encounter_creature_flavor_text,
                encounter_creature_personality,
                encounter_creature_quirks,
                encounter_creature_motivations,
                encounter_creature_secrets,
                encounter_creature_tactics,
                encounter_creature_gm_notes,
                team,
                controller_encounter_creature_id,
                summon_type,
                creature_number,
                creature_initiative,
                v_current,
                v_max,
                v_temp,
                v_max_temp,
                encounter_creature_dead,
                spell_list_prepared,
                mana,
                aps,
                aps_total,
                x_pos,
                z_pos,
                gender,
                voice_id,
            };
    if (encounterCreatureMonsterTemplateIdInput) {
        payload.encounter_creature_monster_template_id = encounterCreatureMonsterTemplateIdInput.value;
    }
    if (merchantFavorInput) {
        payload.merchant_favor = merchantFavorInput.value;
    }

    // Save baseline — the values last painted or saved (renderEditEncounterCreatureForm emits them;
    // see its SAVE BASELINE note). This handler re-POSTs every field on every change, and the form is
    // painted from a cache that can lag a recent edit, so without this the endpoint cannot tell "the
    // user set this" from "this form never heard about the newer value" and silently reverts the latter
    // (owner's summoned ghoul lost its controller + team that way). Forwarded verbatim; the endpoint
    // writes a column only when the submitted value differs from its baseline. Absent → last-write-wins.
    const saveBaselineRaw = formControlValue(container, '#input_hidden_mec_save_baseline');
    if (saveBaselineRaw) {
        try {
            payload._save_baseline = JSON.parse(saveBaselineRaw);
        } catch (e) {
            console.error('modifyEncounterCreature: unreadable save baseline; falling back to last-write-wins', e);
        }
    }

    try {
        const painted = recordOf(payload._save_baseline);
        const template = String(payload.encounter_creature_monster_template_id ?? '').trim().toUpperCase();
        const templateChanged = painted && template !== String(painted.encounter_creature_monster_template_id ?? '').trim().toUpperCase();
        if (templateChanged) {
            try {
                const host = creatureActionHost();
                host.ftSeedCasterFromEncounterCache?.(encounter_creature_id);
                const held = recordOf(host.FateTide?.casterCache?.get(encounter_creature_id));
                const itemId = String(encounterCreatureField(recordOf(held?.encounter_creatures), 'item_id') ?? '');
                if (!itemId) throw new Error('modify-template:source-identity-missing');
                const encounterId = String(encounterCreatureField(recordOf(held?.encounter_creatures), 'encounter_id') ?? '');
                const encounter = recordOf(host.FateTide?.encounterCache?.get(encounterId)) ?? held ?? {};
                const maximum = await withAsyncDeadline(() => creatureDefinitionMaximum(itemId, template || null, encounter), 20_000);
                Object.assign(payload, templateChangeVitality(maximum, payload, painted));
            } catch (error) {
                if (modifySaveEpochs.get(container) !== saveEpoch) return;
                refuseUnported(error instanceof Error && error.name === 'TimeoutError' ? 'modify-template:source-timeout'
                    : error instanceof Error ? error.message : 'modify-template:source-unavailable');
                return;
            }
        }
        // A newer change owns this form's pending decision while source hydration is in flight.
        if (modifySaveEpochs.get(container) !== saveEpoch) return;
        const response = await fetch(`${BASE_URL}options/tools/encounter_creatures/modify_encounter_creature_inc.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.text();

        if (painted && modifySaveEpochs.get(container) === saveEpoch) {
            // The form stays mounted. Advance these baselines after every save so earlier vitality
            // edits are not treated as newly typed overrides on the next template change. Use the
            // saved payload, never a lagging cache; other protected columns keep their painted baseline.
            for (const key of ['v_max', 'v_current', 'encounter_creature_monster_template_id']) {
                if (payload[key] === undefined) continue;
                const input = container.querySelector<HTMLInputElement>(`#${key}`);
                if (templateChanged && input) input.value = String(payload[key]);
                painted[key] = String(payload[key]);
            }
            const baselineInput = container.querySelector<HTMLInputElement>('#input_hidden_mec_save_baseline');
            if (baselineInput) baselineInput.value = JSON.stringify(painted);
        }

        document.getElementById('div_refresh_side_form')!.innerHTML = result;

        loadPostFunctions();

        ftDebugLog('modifyEncounterCreature', { updated: true });

    } catch (error) {
        console.error("Error in modifyEncounterCreature:", error);
    }
}

async function randomizeEncounterCreatureName(event: Event): Promise<void> {
    // Find the closest div containing the button that triggered this function
    const container = event.target instanceof Element ? event.target.closest<HTMLElement>('.encounter-creature-container-form') : null;

    if (!container) {
        console.error('No valid encounter creature container found.');
        return;
    }

    const encounter_creature_id = formControlValue(container, '#input_hidden_encounter_creature_id');
    const gender = formControlValue(container, '#gender');
    const encounter_creature_name = container.querySelector<HTMLInputElement>('#encounter_creature_name');
    if (!encounter_creature_name) return;

    // prelim check
    if(
        !checkIfValidUUID(encounter_creature_id)
    ){
        return;
    }


    try {
        await window.fetchAndCacheCaster?.(encounter_creature_id);
        const caster = creatureActionHost().FateTide?.casterCache?.get(encounter_creature_id);
        if (!caster) throw new Error('The creature could not be loaded for name generation.');
        const tables = creatureNameTables(caster, gender);
        const endpoint = `${BASE_URL}options/tools/encounter_creatures/random_name_inputs_inc.php`;
        const inputs = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ encounter_creature_id, table_ids: [...new Set([...tables.first, tables.last].filter(Boolean))] }) });
        if (!inputs.ok) throw new Error('The name tables could not be loaded.');
        const ranges = await inputs.json() as { rows: CreatureActionRecord[] };
        const name = generateCreatureName(tables, ranges.rows);
        const response = await fetch(`${BASE_URL}options/tools/encounter_creatures/randomize_encounter_creature_name.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                encounter_creature_id,
                name,
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.text();

        // update value of name
        encounter_creature_name.value = result;

        // force change
        forceInputChange(encounter_creature_name);

        // Refresh
        loadPostFunctions();

    } catch (error) {
        console.error("Error in randomizeEncounterCreatureName:", error);
        displayWarning(error instanceof Error ? error.message : 'The name could not be generated.');
    }
}

/** The resource truth seam (websocket/resource-truth.ts ftResourceAdjustTruth): the shared actor-row loader gets the row — held, its
 * encounter loaded, or the encounter-less row itself — the browser decides, resource_truth_commit_inc.php lazily saves; a refusal is
 * shown in words. Never a legacy post (N6 2026-10-01: the adjusts/*.php endpoints are gone). */
type ResourceTruthWindow = { ftResourceAdjustTruth?: (kind: string, actorId: string, input: string | null,
    opts?: { encounterId?: string; rollPrivate?: number; delta?: number }) => Promise<unknown> };
function resourceAdjust(kind: string, actorId: string, delta: number, sl: { encounter_id?: unknown; roll_private?: unknown } | null): Promise<unknown> {
    const adjust = (window as unknown as ResourceTruthWindow).ftResourceAdjustTruth;
    if (!adjust) { displayWarning('This adjustment is not available on this page — reload and try again.'); return Promise.resolve(undefined); }
    return adjust(kind, actorId, null, { encounterId: String(sl?.encounter_id ?? ''), rollPrivate: Number(sl?.roll_private ?? 0) || 0, delta });
}

async function addAPToCreature(): Promise<void> {
    const sl = getSidelogContext();
    await resourceAdjust('add_ap', String(sl?.caster_id ?? ''), 1, sl);
}

async function addReactionToCreature(): Promise<void> {

    // The #caster_id hidden input was removed (cache pivot 2026-06-22). queryDataInputs reads payload
    // values by DOM element id, so the old bare `caster_id` (and friends) reference both threw a
    // ReferenceError in this strict-mode bundle AND could never see the cache-based caster. Source the
    // context from the cache-aware sidelog reader and POST literal values with queryData.
    const sl = getSidelogContext();
    if (!sl) return;

    await resourceAdjust('add_reaction', String(sl.caster_id ?? ''), 1, sl);

}

/** The GM life tools' truth seam (websocket/status-truth.ts ftCommitCreatureLife); a page without it says so instead of doing nothing. */
function lifeSeam(): NonNullable<ReturnType<typeof creatureActionHost>['ftCommitCreatureLife']> | null {
    const fn = creatureActionHost().ftCommitCreatureLife;
    if (typeof fn === 'function') return fn;
    displayWarning('This GM tool is not available on this page — reload and try again.');
    return null;
}

async function killCreature(encounter_creature_id: string): Promise<void> {

    const sl = getSidelogContext();
    if (!sl) return;
    const caster_id = String(creatureActionHost().ftGetActiveCasterId?.() ?? '').trim();

    // Client truth only (status_truth_commit_inc.php — the target row's authority, the actor revision, the death pipeline run once
    // from a stored work record): the shared actor-row loader gets the row; a refusal is shown in words. The legacy
    // kill_encounter_creature_inc.php is gone (N6 2026-10-01, R-066).
    await lifeSeam()?.({ actorId: encounter_creature_id, action: 'kill', killerId: caster_id, encounterId: String(sl.encounter_id ?? '') || undefined });
}

async function destroyCreature(encounter_creature_id: string): Promise<void> {

    const sl = getSidelogContext();
    if (!sl) return;
    // Client truth only (N6 2026-10-01, R-066: destroy_encounter_creature_inc.php is gone).
    await lifeSeam()?.({ actorId: encounter_creature_id, action: 'destroy', encounterId: String(sl.encounter_id ?? '') || undefined });
}

async function fleeCreature(encounter_creature_id: string): Promise<void> {
    const sl = getSidelogContext();
    if (!sl) return;
    await lifeSeam()?.({ actorId: encounter_creature_id, action: 'flee', encounterId: String(sl.encounter_id ?? '') || undefined });
}

async function creatureToolCaster(id: string): Promise<CreatureActionRecord> {
    if (!window.fetchAndCacheCaster) throw new Error('Reload the page to load this creature.');
    await window.fetchAndCacheCaster(id, { skipEncounterSeed: true, throwOnError: true });
    const row = recordOf(creatureActionHost().FateTide?.casterCache?.get(id));
    if (!row || window.ftRequireEntityBase?.(id) === false) throw new Error('The creature could not be loaded.');
    return row;
}

const swarmReinforcements = new Map<string, Promise<void>>();
/** Serialize per creature so a second click decides after the first save and fresh read. */
async function reinforceSwarm(encounter_creature_id: string, count: number | 'full'): Promise<void> {
    const id = encounter_creature_id.toUpperCase();
    const next = (swarmReinforcements.get(id) ?? Promise.resolve()).then(async () => {
        const row = await creatureToolCaster(id);
        const inputs = await fetch(BASE_URL + `options/api/base_entity_inc.php?entity_id=${encodeURIComponent(id)}&entity=creature&cold=0`);
        const base = recordOf(recordOf(await inputs.json())?.base);
        if (!inputs.ok || !Array.isArray(base?.encounter_creature_swarm_states)) throw new Error('The swarm state could not be loaded.');
        const state = base.encounter_creature_swarm_states.map(recordOf).find(state => state
            && String(readItemField(state, 'encounter_creature_swarm_state_encounter_creature_id')).toUpperCase() === id
            && Number(readItemField(state, 'encounter_creature_swarm_state_deleted') ?? 0) === 0) ?? null;
        const plan = planSwarmReinforcement(row, state, count);
        const response = await fetch(BASE_URL + 'options/tools/encounter_creatures/gm_actions/reinforce_swarm_inc.php', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            // The bundle ships first; old PHP still understands this count during that transition.
            body: JSON.stringify({ encounter_creature_id: id, count: String(count), ...plan }),
        });
        const result = recordOf(await response.json());
        if (!response.ok || result?.ok !== true) throw new Error(String(result?.error ?? 'The swarm could not be reinforced.'));
        await creatureToolCaster(id);
    }).catch(error => displayWarning(error instanceof Error ? error.message : String(error)));
    swarmReinforcements.set(id, next);
    await next;
    if (swarmReinforcements.get(id) === next) swarmReinforcements.delete(id);
}

async function copyPlayerCharacter(item_id: string): Promise<void> {
    openSideLog();
    try {
        const caster = await creatureToolCaster(item_id.toUpperCase());
        const initial = characterCopyVitals(caster);
        await queryData(BASE_URL + 'options/tools/player_characters/copy/copy_player_character_inc.php',
            'div_sidelog_warning', { item_id, initial });
    } catch (error) { displayWarning(error instanceof Error ? error.message : String(error)); }
}

async function resurrectCreature(encounter_creature_id: string): Promise<void> {

    const sl = getSidelogContext();
    if (!sl) return;
    // Client truth only (N6 2026-10-01, R-066: resurrect_encounter_creature_inc.php is gone).
    await lifeSeam()?.({ actorId: encounter_creature_id, action: 'resurrect', encounterId: String(sl.encounter_id ?? '') || undefined });
}

/**
 * Resolve a summon's controlling (master) creature id from the encounterArr cache 'truth': the summon
 * row's `controller_encounter_creature_id`. Pure cache pointer (UUID) — NO hidden inputs, NO sidelog/PHP
 * seed. Seeds the row out of the loaded encounter blob first (ftSeedCasterFromEncounterCache aliases the
 * canonical encounter_creatures_arr row into casterCache), so it resolves even on the very first summon
 * click before the summon's own sheet has loaded. Returns '' when the controller can't be read from cache
 * (e.g. a standalone sheet with no loaded encounter blob); the ftGetMasterCasterId() cache-truth fallback
 * then covers the same derivation on demand. No hidden inputs, no PHP seed in this path.
 */
function ftResolveSummonControllerId(summonId: string): string {
    if (!checkIfValidUUID(summonId)) return '';
    const host = creatureActionHost();
    try {
        if (typeof host.ftSeedCasterFromEncounterCache === 'function') {
            host.ftSeedCasterFromEncounterCache(summonId);
        }
        const caster = recordOf(host.FateTide?.casterCache?.get(summonId));
        let v = recordOf(caster?.encounter_creatures)?.controller_encounter_creature_id;
        if (Array.isArray(v)) v = v[0];
        const controller = (v === null || v === undefined) ? '' : String(v).trim();
        return checkIfValidUUID(controller) ? controller : '';
    } catch (e) {
        return '';
    }
}

function setCasterSummon(encounter_creature_id: string): void {

    // SUMMON CONTROL — the one exception where the active caster ≠ the master caster
    // (caster cache pivot 2026-06-22). Route through the cache funnel so
    // window.FateTide.activeCasterId = the summon (websocket subscribed), while masterCasterId stays
    // on the controlling PC. No { asMaster } keeps the master untouched. setCasterSummon only fires on
    // a user click, so ftSetActiveCaster is loaded.
    const host = creatureActionHost();

    // ESTABLISH THE MASTER (home) CASTER as a CACHE VARIABLE pointing at the encounterArr cache 'truth'
    // — not the sidelog hidden-input seed. The summon row carries controller_encounter_creature_id = the
    // controlling PC; point masterCasterId at that UUID so leaving the summons tab reverts control
    // (ftAssertSelfCasterOnTab matches the clicked card's id against masterCasterId). Without this, on
    // multi-caster surfaces (the encounter / map page) the PHP seed never set masterCasterId — it was ''
    // the whole session — so the revert short-circuited and you stayed stuck controlling the summon
    // after pivoting to any non-summon tab (owner-reported 2026-06-29). Idempotent on surfaces where the
    // seed already set master to this same PC; self-heals a stale/empty one from cache truth.
    const controllerId = ftResolveSummonControllerId(String(encounter_creature_id || '').trim());
    if (controllerId) {
        host.FateTide = host.FateTide || {};
        host.FateTide.masterCasterId = controllerId;
    }

    if (typeof host.ftSetActiveCaster === 'function') {
        void host.ftSetActiveCaster(encounter_creature_id);
    } else {
        void subscribeToCaster();
    }

    // Persist the summon selection in the per-tab JS cache (sessionStorage via ftSetSelectedSummon),
    // NOT a global cookie (de-cookie 2026-06-24): it survives a same-tab reload yet can't leak the
    // selection to other sheets or past the session, which is what caused the deleted-summon hijack.
    if (typeof host.ftSetSelectedSummon === 'function') {
        host.ftSetSelectedSummon(encounter_creature_id);
    }

    // Summons lazy pane: the sub-sheet is no longer rendered at page load (_summons_inc.php
    // emits an empty wrapper) — first activation fetches it.
    void ftEnsureSummonSheetLoaded(encounter_creature_id);
}

/**
 * Summons lazy pane loader (plans/summons-ts-pane-and-websocket-push.md Phase 1).
 * _summons_inc.php renders each summon as an EMPTY #div_caster_sheet_ec_<hex> wrapper; the first
 * activation fills it via refreshCasterSheet() — the same request-scoped endpoint
 * (table_encounter_creatures_inc.php) every later refresh of the card uses, so initial paint and
 * refresh share one code path and the old page-scope table_item_inc.php include is gone.
 */
async function ftEnsureSummonSheetLoaded(encounter_creature_id: string): Promise<void> {
    if (!checkIfValidUUID(encounter_creature_id)) return;
    const hex = String(encounter_creature_id).replace(/[^a-fA-F0-9]/gi, '');
    const host = document.getElementById(`div_caster_sheet_ec_${hex}`);
    if (!host || !host.hasAttribute('data-ft-summon-ec') || host.dataset.ftSummonLoaded === '1') {
        return;
    }
    host.dataset.ftSummonLoaded = '1'; // claim before the await so a double-click can't double-fetch
    try {
        await refreshCasterSheet(encounter_creature_id);
    } catch (e) {
        delete host.dataset.ftSummonLoaded; // allow a retry on the next activation
        console.error('summon sheet load failed:', e);
    }
}

// addAttackToCaster
async function addAttackToCaster(): Promise<void> {

    const sl = getSidelogContext();
    if (!sl) return;
    const caster_id = creatureActionHost().ftGetActiveCasterId?.() ?? '';

    await resourceAdjust('weapon_attack', caster_id, 1, sl);

}

// addAttackToCaster
async function reduceAttackToCaster(): Promise<void> {

    const sl = getSidelogContext();
    if (!sl) return;
    const caster_id = creatureActionHost().ftGetActiveCasterId?.() ?? '';

    await resourceAdjust('weapon_attack', caster_id, -1, sl);
}

// addHeadAttackToCaster / reduceHeadAttackToCaster
async function addHeadAttackToCaster(): Promise<void> {

    const sl = getSidelogContext();
    if (!sl) return;
    const caster_id = creatureActionHost().ftGetActiveCasterId?.() ?? '';

    await resourceAdjust('head_attack', caster_id, 1, sl);
}

async function reduceHeadAttackToCaster(): Promise<void> {

    const sl = getSidelogContext();
    if (!sl) return;
    const caster_id = creatureActionHost().ftGetActiveCasterId?.() ?? '';

    await resourceAdjust('head_attack', caster_id, -1, sl);
}

async function update_encounter_creature_pic_id(encounter_creature_id: string, pic_type: string, pic_id: string, button: HTMLElement): Promise<void> {
    // Find the closest carousel container
    const container = button.closest('.carousel');

    if (!container) {
        console.error('No valid carousel container found.');
        return;
    }

    // Validate UUID
    if (!checkIfValidUUID(encounter_creature_id)) {
        console.error('Invalid encounter_creature_id.');
        return;
    }

    await queryData(
        BASE_URL + "options/tools/encounter_creatures/update_encounter_creature_pic_id.php",
        'div_sidelog_warning',
        {
            encounter_creature_id,
            pic_type,
            pic_id,
        }
    );

    // Update the appropriate hidden input based on pic_type
    const inputSelector = pic_type === 'encounter_creature_presentation_pic_id' ? '.carousel-presentation-pic-id' : '.carousel-thumbnail-pic-id';
    const buttonSelector = pic_type === 'encounter_creature_presentation_pic_id' ? '.set-presentation-pic' : '.set-thumbnail-pic';
    const input = container.querySelector<HTMLInputElement>(inputSelector);
    if (input) {
        input.value = pic_id;
    }

    // Update button visibility for all items
    container.querySelectorAll<HTMLElement>('.carousel-item').forEach((item) => {
        const targetButton = item.querySelector<HTMLElement>(`${buttonSelector}[onclick*="'${pic_id}'"]`);
        if (targetButton) {
            targetButton.style.display = 'none';
        }
        const otherButtons = item.querySelectorAll<HTMLElement>(`${buttonSelector}:not([onclick*="'${pic_id}'"])`);
        otherButtons.forEach((btn) => {
            btn.style.display = '';
        });
    });

}

async function updatePreferredSpeedType(speed_type_item_id: string, caster_id_override = ''): Promise<void> {

    const sl_context = getSidelogContext();
    if (!sl_context) {
        console.error('Failed to get sl_context for updatePreferredSpeedType');
        return;
    }

    const {
        roll_private,
        campaign_id,
        location_id,
        encounter_id,
        caster_id: active_caster_id,
    } = sl_context;

    // A movement selector can belong to a GM-controlled creature card/action bar that is not the
    // ambient active caster. Prefer that explicit card-scoped encounter-creature id when supplied;
    // the sidelog caster remains the fallback for legacy PHP onclick callers.
    const caster_id = caster_id_override || active_caster_id;


    // Validate required fields
    const rules = {
        roll_private: { type: 'enum', value: roll_private, allowed: ['0', '1', '2'] },
        caster_id: { type: 'uuid', value: caster_id },
        speed_type_item_id: { type: 'uuid', value: speed_type_item_id },
    };
    const validationResult = validateContext(rules, 'updatePreferredSpeedType');
    if (!validationResult.valid) {
        console.error(validationResult.error);
        return;
    }


    // fbspeedlag: every surface a drag reads (the held encounter row, the caster card row, the token datasets) shows the switch BEFORE
    // the (slow) save; a refused save falls back to the newest selection still standing.
    const cache = creatureActionHost().FateTide;
    const actor = String(caster_id), encounterKey = String(encounter_id ?? '');
    const targets = () => preferredSpeedTargets(
        recordOf(cache?.encounterCache?.get(encounterKey) ?? cache?.encounterCache?.get(encounterKey.toUpperCase())),
        cache?.casterCache?.get(actor) ?? cache?.casterCache?.get(actor.toUpperCase()), actor, document);
    const settle = switchPreferredSpeed(`${encounterKey.toUpperCase()}|${actor.toUpperCase()}`, targets, speed_type_item_id);
    ftDebugLog('xz speed', { preferredSpeedHeld: true, speed_type_item_id, newMode: PREFERRED_SPEED_TO_MODE[speed_type_item_id] || 'free' });

    let answer: string | null = null;
    try {
        const sink = ftCurrentTruthSink();
        answer = sink ? JSON.stringify(await sink.commit({ schema: 'janus.movement.preference.v1', operation_id: crypto.randomUUID().toUpperCase(),
            encounter_id, actor_id: actor, preferred_speed_type: speed_type_item_id }, {})) : await queryData(
            BASE_URL + "options/tools/encounter_creatures/update_preferred_speed_type.php",
            'div_sidelog_warning',
            {
                roll_private,
                campaign_id,
                location_id,
                encounter_id,
                caster_id,
                speed_type_item_id,
            }
        );
    } finally {
        settle(preferredSpeedSaveRefused(answer) ? 'refused' : 'saved');
    }
}

// ===========================================================================================
// Window attachment — the public surface the original top-level functions exposed. All
// runtime-invoked (PHP onclick / form-button change / object-card + info-panel + player-character-sheet
// render-bundle onclick strings / template.js shown.bs.tab + loadPostFunctions callers). Every name
// has at least one caller OUTSIDE the carved range, so NONE stays module-local.
// ===========================================================================================
declare global {
    interface Window {
        modifyEncounterCreature: typeof modifyEncounterCreature;
        randomizeEncounterCreatureName: typeof randomizeEncounterCreatureName;
        addAPToCreature: typeof addAPToCreature;
        addReactionToCreature: typeof addReactionToCreature;
        killCreature: typeof killCreature;
        destroyCreature: typeof destroyCreature;
        fleeCreature: typeof fleeCreature;
        resurrectCreature: typeof resurrectCreature;
        reinforceSwarm: typeof reinforceSwarm;
        copyPlayerCharacter: typeof copyPlayerCharacter;
        setCasterSummon: typeof setCasterSummon;
        ftEnsureSummonSheetLoaded: typeof ftEnsureSummonSheetLoaded;
        addAttackToCaster: typeof addAttackToCaster;
        reduceAttackToCaster: typeof reduceAttackToCaster;
        addHeadAttackToCaster: typeof addHeadAttackToCaster;
        reduceHeadAttackToCaster: typeof reduceHeadAttackToCaster;
        update_encounter_creature_pic_id: typeof update_encounter_creature_pic_id;
        updatePreferredSpeedType: typeof updatePreferredSpeedType;
    }
}

Object.assign(window, {
    modifyEncounterCreature,
    randomizeEncounterCreatureName,
    addAPToCreature,
    addReactionToCreature,
    killCreature,
    destroyCreature,
    fleeCreature,
    resurrectCreature,
    reinforceSwarm,
    copyPlayerCharacter,
    setCasterSummon,
    ftEnsureSummonSheetLoaded,
    addAttackToCaster,
    reduceAttackToCaster,
    addHeadAttackToCaster,
    reduceHeadAttackToCaster,
    update_encounter_creature_pic_id,
    updatePreferredSpeedType,
});

export {};
